import React, { useCallback, useMemo, useState } from 'react'

import { IBuffetGeneralReportResult } from 'core/api/dto/BuffetGeneralReportDto'
import { SetURLSearchParams } from 'react-router-dom'
import { SubTableRepresentFull } from './SubTableRepresentFull'
import { useTranslation } from 'react-i18next'
import { IHeaderData } from 'components/AppTable/AppTable'
import { headerDataFirstValue } from 'components/AppTable/components/headerDataFirstValue'
import { AppTableDndMobile } from 'components/AppTable/AppTableDndMobile'
import { formatNumber } from 'shared/lib/hooks/formatNumber'
import { TemperatureDisplay } from 'shared/ui/TemperatureDisplay'
import {
  ImagePreviewWrapper,
  ImagePreviewWrapperSizeEnum,
} from 'shared/ui/ImagePreviewWrapper/ImagePreviewWrapper'

const headerData = [
  {
    title: 'Наименование',
    colWidth: '22%',
    sort: 'name',
    order: 0,
    startPosition: 0,
  },
  {
    title: 'Категория',
    colWidth: '3%',
    sort: 'categoryName',
    order: 1,
    startPosition: 1,
  },
  { title: 'Станция', colWidth: '10%', order: 2, startPosition: 2 },
  { title: 'Заменённое блюдо', colWidth: '10%', order: 3, startPosition: 3 },
  { title: 'Дата', colWidth: '15%', order: 4, startPosition: 4 },
  { title: 'Время', colWidth: '3%', order: 5, startPosition: 5 },
  { title: 'Название план-меню', colWidth: '3%', order: 6, startPosition: 6 },
  { title: 'Тип гостя', colWidth: '3%', order: 7, startPosition: 7 },
  { title: 'Тип замера', colWidth: '3%', order: 8, startPosition: 8 },
  { title: 'Значение типа замера', colWidth: '3%', order: 9, startPosition: 9 },
  {
    title: 'Кол-во замеров',
    colWidth: '3%',
    sort: 'dimensionsCount',
    order: 10,
    startPosition: 10,
  },
  {
    title: 'Порядковый номер замера',
    colWidth: '3%',
    order: 11,
    startPosition: 11,
  },
  {
    title: 'Заявленный вес в план-меню, кг',
    colWidth: '3%',
    sort: 'plannedWeight',
    order: 12,
    startPosition: 12,
  },
  {
    title: 'Заявленное кол-во персон, человек',
    colWidth: '3%',
    order: 13,
    startPosition: 13,
  },
  {
    title: 'Заявленный вес на персону, кг',
    colWidth: '3%',
    order: 14,
    startPosition: 14,
  },
  { title: 'Название тары', colWidth: '25%', order: 15, startPosition: 15 },
  { title: 'Вес тары, кг', colWidth: '3%', order: 16, startPosition: 16 },
  {
    title: 'Фактический общий вес, кг',
    colWidth: '3%',
    order: 17,
    startPosition: 17,
  },
  {
    title: 'Фактический вес без тары, кг',
    colWidth: '3%',
    sort: 'actualWeight',
    order: 18,
    startPosition: 18,
  },
  {
    title: 'Фактическое кол-во персон, человек',
    colWidth: '3%',
    sort: 'actualPersonsAmount',
    order: 19,
    startPosition: 19,
  },
  {
    title: 'Фактический вес на персону, кг',
    colWidth: '3%',
    sort: 'actualWeightPerPerson',
    order: 20,
    startPosition: 20,
  },
  {
    title: 'Разница кол-во персон, человек',
    colWidth: '3%',
    sort: 'personsAmountDiff',
    order: 21,
    startPosition: 21,
  },
  { title: 'Разница вес, кг', colWidth: '3%', order: 22, startPosition: 22 },
  {
    title: 'Эталонная температура продукта мин - макс, °C',
    colWidth: '3%',
    sort: 'standardTempMax',
    order: 23,
    startPosition: 23,
  },
  {
    title: 'Фактическая температура продукта мин - сред- макс, °C',
    colWidth: '25%',
    sort: 'temperatureAvg',
    order: 24,
    startPosition: 24,
  },
  {
    title: 'Соответствие продукта темп режиму',
    colWidth: '3%',
    order: 25,
    startPosition: 25,
  },
  { title: 'Фото', colWidth: '5%', order: 26, startPosition: 26 },
  {
    title: 'Соответствие эталонности',
    colWidth: '10%',
    order: 27,
    startPosition: 27,
    sort: 'aiPredictions',
  },
  {
    title: 'Тип несоответствия',
    colWidth: '400px',
    order: 28,
    startPosition: 28,
    sort: 'complianceWithStandards',
  },
]

interface IReportFullProps {
  reportData: IBuffetGeneralReportResult[]
  setSearchParams: SetURLSearchParams
  setCurrentSortValue: React.Dispatch<React.SetStateAction<string | null>>
}

export const ReportFull = ({
  reportData,
  setSearchParams,
  setCurrentSortValue,
}: IReportFullProps) => {
  const [toggleAllState, setToggleAllState] = useState<number[]>([])
  const { t } = useTranslation()
  const [headerDataDnd, setHeaderData] = useState<IHeaderData[]>(
    headerDataFirstValue('report-table-full', headerData),
  )

  const tableDataSelectors = useMemo(
    () => [
      {
        renderItem: (item: IBuffetGeneralReportResult, index: number) => (
          <div
            className={
              index
                ? 'violet-sticky'
                : 'buffet-report-page-table-category-title'
            }
          >
            {t(item?.name)}
          </div>
        ),
      },
      {
        renderItem: (item: IBuffetGeneralReportResult) => (
          <div>{item?.categoryName}</div>
        ),
      },
      {
        renderItem: () => <div />,
      },
      {
        renderItem: () => <div />,
      },
      {
        renderItem: () => <div />,
      },
      {
        renderItem: () => <div />,
      },
      {
        renderItem: () => <div />,
      },
      {
        renderItem: () => <div className={`text-end`}></div>,
      },
      {
        renderItem: () => <div className={`text-end`}></div>,
      },
      {
        renderItem: () => <div className={`text-end`}></div>,
      },
      {
        renderItem: (item: IBuffetGeneralReportResult) => (
          <div className={`text-end`}>{item.dimensionsCount}</div>
        ),
      },
      {
        renderItem: () => <div className={`text-end`}></div>,
      },
      {
        renderItem: (item: IBuffetGeneralReportResult) => (
          <div className={`text-end`}>{formatNumber(item?.plannedWeight)}</div>
        ),
      },
      {
        renderItem: (item: IBuffetGeneralReportResult) => (
          <div className={`text-end`}>{item?.plannedPersonsAmount}</div>
        ),
      },
      {
        renderItem: (item: IBuffetGeneralReportResult) => (
          <div className={`text-end`}>
            {formatNumber(item?.plannedWeightPerPerson)}
          </div>
        ),
      },
      {
        renderItem: () => <div className={`text-end`}></div>,
      },
      {
        renderItem: () => <div className={`text-end`}></div>,
      },
      {
        renderItem: (item: IBuffetGeneralReportResult) => (
          <div className={`text-end`}>
            {formatNumber(item?.dimensionWeight)}
          </div>
        ),
      },
      {
        renderItem: (item: IBuffetGeneralReportResult) => (
          <div className={`text-end`}>{formatNumber(item?.actualWeight)}</div>
        ),
      },
      {
        renderItem: (item: IBuffetGeneralReportResult) => (
          <div className={`text-end`}>{+item?.actualPersonsAmount}</div>
        ),
      },
      {
        renderItem: (item: IBuffetGeneralReportResult) => (
          <div className={`text-end`}>
            {formatNumber(item?.actualWeightPerPerson)}
          </div>
        ),
      },
      {
        renderItem: (item: IBuffetGeneralReportResult) => (
          <div className={`text-end`}>{item?.personsAmountDiff}</div>
        ),
      },
      {
        renderItem: (item: IBuffetGeneralReportResult) => (
          <div className={`text-end`}> {formatNumber(item?.weightDiff)}</div>
        ),
      },
      {
        renderItem: (item: IBuffetGeneralReportResult) => (
          <>
            <div className={`text-end`}>
              {item.standardTempMin}
              {item.standardTempMin && item.standardTempMax && <span> - </span>}
              {item.standardTempMax}
            </div>
          </>
        ),
      },
      {
        renderItem: (item: IBuffetGeneralReportResult) => (
          <div className={`text-end`}>
            <TemperatureDisplay item={item} />
          </div>
        ),
      },
      {
        renderItem: () => <div className={`text-end`}></div>,
      },
      {
        renderItem: (item: IBuffetGeneralReportResult) => (
          <div className={`text-end`}>
            <ImagePreviewWrapper
              className={'report-image-preview'}
              onClick={() => {
                setSearchParams((sp) => {
                  sp.set('report-image-id', item.id.toString())
                  return sp
                })
              }}
              isReportPageItem={true}
              image={item.imageOnS3 || item.image || null}
              size={ImagePreviewWrapperSizeEnum.MEDIUM}
            />
          </div>
        ),
      },
      {
        renderItem: () => <div className={`text-end`}></div>,
      },
      {
        renderItem: () => <div className={`text-end`}></div>,
      },
    ],
    [t, setSearchParams],
  )
  const subTableRepresentFn = useCallback(
    (
      item: IBuffetGeneralReportResult,
      currentCol?: number,
      itemIndex?: number,
      onTouchStart?: (e: React.TouchEvent) => void,
      onTouchMove?: (e: React.TouchEvent) => void,
      width?: number,
      toggleAllState?: number[],
      headerData?: IHeaderData[],
      ref?: React.Ref<HTMLTableRowElement>,
    ) => (
      <SubTableRepresentFull
        item={item}
        currentCol={currentCol}
        itemIndex={itemIndex}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        width={width}
        toggleAllState={toggleAllState}
        toggleAllStateFullLenght={reportData.length}
        headerData={headerData}
        ref={ref}
      />
    ),
    [reportData.length],
  )

  return (
    <React.Fragment>
      {reportData && (
        <AppTableDndMobile<
          IBuffetGeneralReportResult,
          keyof IBuffetGeneralReportResult
        >
          setHeaderData={setHeaderData}
          toggleAllState={toggleAllState}
          setToggleAllState={setToggleAllState}
          tableName="report-table-full"
          data={{
            count: 0,
            results: reportData,
            previous: '',
            next: '',
          }}
          tableWrapperClassName={'buffet-report-table'}
          tableCellClassName={'buffet-report-page-table-cell'}
          rowClassName={'buffet-report-page-table-category'}
          setCurrentSortFn={setCurrentSortValue}
          tableDataSelectors={tableDataSelectors}
          subTableRepresent={subTableRepresentFn}
          headerData={headerDataDnd}
        />
      )}
    </React.Fragment>
  )
}
